/**
*****************************************
*                                       *
* NAGARRO  - CSX - CONFIDENTIAL         *
*                                       *
*****************************************
*/

import { OverlayPanel } from 'primeng/overlaypanel';
import {
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';

import { AddToPendingService } from './../../../terminal/shared/services/add-to-pending.service';
import { AfterViewInit } from '@angular/core';
import { ConfigService } from './../../services/config.service';
import { DataStoreService } from './../../services/datastore.service';
import { EditMode } from '../../model/edit-mode.interface';
import { EditModeService } from '../../services/edit-mode.service';
import { LmsEventDetails } from './../../model/lms-event-details.model';
import { LmsMenuService } from './../../services/lms-menu.service';
import { LmsTabsDragDropService } from './../../services/lms-tabs-drag-drop.service';
import { MenuItem } from '../../model/menu-item.model';
import { MessageDetails } from './../../model/message-details.model';
import { MessageHandlerService } from './../../services/message-handler.service';
import { PopupService } from '../../../core/services/popup.service';
import { Router } from '@angular/router';
import { StaticDataStoreService } from './../../services/static-datastore.service';
import { Tab } from './../../model/tab.model';
import { TabService } from './../../services/tab.service';
import { environment } from './../../../../environments/environment';
import { LmsCarouselComponent } from '../lms-carousel/lms-carousel.component';

/**
 * @export
 * @class LmsTabsComponent
 * @implements {OnInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
    selector: 'lms-tabs',
    templateUrl: './lms-tabs.component.html',
    styleUrls: ['./lms-tabs.component.scss']
})

export class LmsTabsComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

    @Input() tabs: Tab[];
    @Input() editMode: Boolean;

    @Output() lmsTabChange = new EventEmitter<LmsEventDetails>();

    editModeStatus: EditMode;

    searchText: string;

    filteredTabs: Tab[];

    subTypes: string[];

    firstVisible = 0;

    panelSelectedIndex = 0;

    selectedTab: Tab;

    panelVisible = false;

    tabOverlay: OverlayPanel;

    pcrousal: LmsCarouselComponent;

    tabLevel: number;
    tabLevelDialog: number;

    private defaultLocoInitial = 'CSXT';

    showWarnPop = false;

    warnMessage: string;

    isChangeSearch = false;

    showHeader: boolean;

    @ViewChild('pcarousal', {read: LmsCarouselComponent}) pcarousalRef: LmsCarouselComponent;

    /**
     * Creates an instance of LmsTabsComponent.
     * @param {ConfigService} configService
     * @param {LmsMenuService} lmsMenuService
     * @param {LmsTabsDragDropService} lmsTabsDragDropService
     * @param {DataStoreService} dataStoreService
     * @memberOf LmsTabsComponent
     */
    constructor(private configService: ConfigService,
        private lmsMenuService: LmsMenuService,
        private lmsTabsDragDropService: LmsTabsDragDropService,
        private dataStoreService: DataStoreService,
        private staticDataStoreService: StaticDataStoreService,
        private router: Router,
        private editModeService: EditModeService,
        private addToPendingService: AddToPendingService,
        private tabService: TabService,
        private messageHandlerService: MessageHandlerService,
        private popUpService: PopupService) {
        this.tabLevel = this.configService.getConfigData().tabIndex.topTabs;
        this.tabLevelDialog = this.configService.getConfigData().tabIndex.dialogBox;

        if (staticDataStoreService.getValue('SHOW_HEADER') == false)
            this.showHeader = false;
        else
            this.showHeader = true;
    }

    /**
     * @private
     * @param {*} data
     * @memberOf LmsTabsComponent
     */
    private setWarnPopupMessage(data: any) {
        this.warnMessage = data && data.message ? data.message : '';
    }

    /**
     * This method invoked when component is load.
     * @memberOf LmsTabsComponent
     */
    ngOnInit() {
        this.init();
        this.isChangeSearch = false;
    }

    /**
     * This method invoked when any change is done in tabs.
     * @param {SimpleChanges} changes
     * @memberOf LmsTabsComponent
     */
    ngOnChanges(changes: SimpleChanges) {
        const tabChanged: boolean = changes['tabs'] && (changes['tabs'].currentValue !== changes['tabs'].previousValue);
        if (tabChanged) {
            this.init();
            this.processCursorPosition();
        }
    }

    ngAfterViewInit() {
        this.processCursorPosition();
        setTimeout(() => {
            this.pcrousal = this.pcarousalRef;
        }, 1000);
    }

    /**
     * Method initialize the selected tab, filter tabs (contains tabs according to type),
     * sub types and trigger search method.
     * @private
     * @memberOf LmsTabsComponent
     */
    private init() {
        if (this.tabs) {
            this.selectedTab = this.tabs.find((tab) => { return tab && tab.selected; });
        }
        if (this.selectedTab) {
            this.filteredTabs = this.tabs.filter((tab) => { return this.selectedTab && this.selectedTab.type === tab.type; });
            this.navCrousal(this.selectedTab, false);
            this.subTypes = (<MenuItem>(this.lmsMenuService.getMenuItem(this.selectedTab.type))).subTypes;
            this.searchText = this.selectedTab && this.selectedTab.tabSearchParameter ?
                this.selectedTab.tabSearchParameter.searchText
                : '';
            this.search(false);
        }
    }

    /**
     * this method is used to set the Carousel movement
     * i.e. a Carousel have number of page as user add element
     * @param {Tab} tab
     * @param {boolean} isEmitted
     * @memberOf LmsTabsComponent
     */
    public navCrousal(tab: Tab, isEmitted: boolean) {
        if (this.filteredTabs) {
            let firstVisible = this.filteredTabs.indexOf(tab);
            if (firstVisible !== -1) {
                firstVisible = firstVisible;
            } else {
                firstVisible = 0;
            }
            setTimeout(() => {
                if (this.pcrousal) {
                    const pageCount = this.selectedTab && this.selectedTab.type !== 'Yard-Local' ? 6 : 4;
                    const newPage = Math.floor((firstVisible) / pageCount);
                    this.pcrousal.firstVisible = firstVisible;
                    this.pcrousal.setPage(newPage);
                }
                this.carouselAdjustment();
            }, 100);
        }
    }

    /**
     * carousel adjustment.
     *
     * @memberof LmsTabsComponent
     */
    carouselAdjustment() {
        if (this.pcrousal && this.pcrousal.differ) {
            const changes = this.pcrousal.differ.diff(this.pcrousal.value);
            if (changes) {
                if (this.pcrousal.value.length && this.pcrousal.firstVisible >= this.pcrousal.value.length) {
                    this.pcrousal.setPage(this.pcrousal.totalPages - 1);
                } else {
                    this.pcrousal.setPage(0);
                }
                this.pcrousal.valuesChanged = true;
                if (this.pcrousal.autoplayInterval) {
                    this.pcrousal.stopAutoplay();
                }
                this.pcrousal.updateMobileDropdown();
                this.pcrousal.updateLinks();
                this.pcrousal.updateDropdown();
            }
        }
    }

    /**
     * This method update data container as per selected tab, isRefersh indicates
     * that data is fetched from service or from datastore.
     * @param {boolean} isRefresh
     * @memberOf LmsTabsComponent
     */
    search(isRefresh: boolean) {
        if (this.searchText && this.searchText.trim().length > 0) {
            this.isChangeSearch = false;
            if (this.editModeService.isEditMode && isRefresh) {
                this.showWarnPop = true;
                this.warnMessage = this.editModeService.editModeMessage ? this.editModeService.editModeMessage : 'Unsaved data will be lost. Do you want to continue?';
                return;
            }
            if (isRefresh) {
                if (this.selectedTab.type === 'Terminal') {
                    this.selectedTab.tabSearchParameter.searchText = this.getConvertedValue(this.searchText);
                    if (this.selectedTab.subType === 'Status') {
                        this.addToPendingService.resetAll(); // nee to do work just for demo (;)
                    }
                } else if (this.selectedTab.type === 'Locomotive') {
                    if (this.isValidLocomotiveId(this.formatLocomotiveId(this.searchText))) {
                        this.searchText = this.formatLocomotiveId(this.searchText);
                    }
                    this.selectedTab.tabSearchParameter.searchText = this.searchText;
                } else if (this.selectedTab.type === 'Train') {
                    if (this.isValidTrainId(this.searchText)) {
                        this.searchText = this.searchText;
                    }
                    this.selectedTab.tabSearchParameter.searchText = this.searchText;
                } else if (this.selectedTab.type === 'Outlying-Points') {
                    this.selectedTab.tabSearchParameter.searchText = this.searchText;
                }
                this.dataStoreService.removeTab(this.selectedTab);
                this.selectedTab.subTypeDetail = {};
                this.selectedTab.subTypeDetail[this.selectedTab.subType.toLowerCase()] = {};
                this.lmsTabChange.emit(
                    <LmsEventDetails>({ type: this.configService.getConfigData().tab.event.refresh, data: this.selectedTab }));
            }
        } else {
            this.searchText = this.selectedTab.tabSearchParameter.searchText;
        }

    }

    /**
     * remove tab from tabstore and corresponding data from datastore.
     * @param {Tab} tab
     * @memberOf LmsTabsComponent
     */
    removeTab(tab: Tab) {
        this.searchText = '';
        if (this.filteredTabs.length === 1) {
            this.addTab(tab.subType);
        } else if (tab.selected) {
            const tabDetails = (this.filteredTabs.indexOf(tab) === 0)
                ? this.filteredTabs[this.filteredTabs.indexOf(tab) + 1] : this.filteredTabs[this.filteredTabs.indexOf(tab) - 1];
            this.router.navigateByUrl(`/${tabDetails.type.toLowerCase()}/${tabDetails.subType.toLowerCase()}/${tabDetails.id}`,
                { skipLocationChange: environment.hideUrl });
        }
        this.lmsTabChange.emit(<LmsEventDetails>({ type: this.configService.getConfigData().tab.event.remove, data: tab }));
        this.dataStoreService.removeTab(tab);
    }

    /**
     * set current selected tab. lmsTabChange event emits when tab is selected.
     * @param {Tab} tab
     * @memberOf LmsTabsComponent
     */
    selectTab(tab: Tab) {
        this.router.navigateByUrl(`/${tab.type.toLowerCase()}/${tab.subType.toLowerCase()}/${tab.id}`,
            { skipLocationChange: environment.hideUrl });
    }

    /**
     * Add new tab for selected subtype and search text.
     * @param {string} subType
     * @memberOf LmsTabsComponent
     */
    addTab(subType: string) {
        const userProfile = this.staticDataStoreService.getValue('userProfile');
        const managerDesk = this.staticDataStoreService.getValue('commonopsPicklist');
        if (this.selectedTab) {
            this.searchText = '';
            if (this.selectedTab.type === 'Outlying-Points') {
                let isDeskPresent = false;
                if (managerDesk && managerDesk.masterData) {
                    for (let i = 0; i < managerDesk.masterData.zoneList.length; i++) {
                        if (userProfile.alertDesk && userProfile.alertDesk.trim().toLowerCase() === managerDesk.masterData.zoneList[i].zoneCode.trim().toLowerCase()) {
                            isDeskPresent = true;
                            break;
                        }
                    }
                }
                if (isDeskPresent) {
                    this.searchText = userProfile.alertDesk;
                }
            }
            document.getElementById('addTabIconTop').focus();
            if (this.searchText) {
                this.router.navigateByUrl(`/${this.selectedTab.type.toLowerCase()}/${subType.toLowerCase()}/${new Date().getTime()}?search=${this.searchText}`,
                    { skipLocationChange: environment.hideUrl });
            } else {
                this.router.navigateByUrl(`/${this.selectedTab.type.toLowerCase()}/${subType.toLowerCase()}/${new Date().getTime()}`,
                    { skipLocationChange: environment.hideUrl });
            }

        }
    }

    /**
     * This will show overlay panel with all the sub types
     * according to current selected tab type.
     * @param {any} event
     * @param {OverlayPanel} op
     * @memberOf LmsTabsComponent
     */
    public overlayPosition(event, op: OverlayPanel) {
        this.tabOverlay = op;
        if (!op.overlayVisible && this.panelVisible === true) {
            this.panelVisible = false;
        }

        if (this.panelVisible) {
            op.hide();
            this.panelVisible = false;
            event.stopPropagation();
            return;
        }
        op.show(event);
        setTimeout(() => {
            const overlayPanel = document.getElementById('add-tab-items').getElementsByClassName('p-overlaypanel');
            const element = document.getElementById('addTabIconTop').parentElement;
            const rect = element.getBoundingClientRect();
            const scrollHeight = window.pageYOffset;
            const top = rect.top + rect.height + scrollHeight + 'px';
            const left = rect.left + 'px';
            const width = rect.width + 'px';
            let style = `display: block; z-index: 1001; visibility: visible;
            opacity: 1.032; top: <<top>>; left: <<left>>; width: <<width>>;`;
            style = style.replace('<<top>>', top);
            style = style.replace('<<left>>', left);
            style = style.replace('<<width>>', width);
            overlayPanel[0].setAttribute('style', style);
            (op.container.children[0].children[0].children[0].firstElementChild as HTMLElement).focus();
        });
        event.stopPropagation();
        this.panelSelectedIndex = 0;
        this.panelVisible = true;
    }

    /**
     * on key up and down
     * select subtypes
     * @param {any} event
     * @param {OverlayPanel} op
     * @memberof LmsTabsComponent
     */
    inPanelKeyUp(event, op: OverlayPanel) {
        const keyCode = event.which || event.keyCode;
        event.stopPropagation();
        const totalChild = op.container.children[0].children[0].children.length;
        switch (keyCode) {
            // down
            case 40:
                if (this.panelSelectedIndex < (totalChild - 1)) {
                    this.panelSelectedIndex++;
                }
                event.preventDefault();
                break;

            // up
            case 38:
                if (this.panelSelectedIndex > 0) {
                    this.panelSelectedIndex--;
                }
                event.preventDefault();
                break;

        }
        (op.container.children[0].children[0].children[this.panelSelectedIndex].firstElementChild as HTMLElement).focus();
    }
    /**
     * This method is used to hide the overlay panel
     * which is comes in open state when user click on
     * plus icon to add more tabs.
     * @param {any} event
     * @param {OverlayPanel} op
     * @returns
     * @memberOf LmsTabsComponent
     */
    public hideOverlayPanel(event, op: OverlayPanel) {
        if (this.panelVisible) {
            op.hide();
            this.panelVisible = false;
            this.tabOverlay = null;
            return;
        }
    }

    /**
     * this code can be used if needed.
     * Please don't delete it for now.
     */
    @HostListener('document:click') hideAddTabPanel() {
        if (this.tabOverlay && this.panelVisible) {
            this.tabOverlay.hide();
            this.panelVisible = false;
            this.tabOverlay = null;
            return;
        }
    }

    /**
     * This method invoked when tab change.
     * @param {LmsEventDetails} lmsEventDetails
     * @memberOf LmsTabsComponent
     */
    lmsEventHandler(lmsEventDetails: LmsEventDetails) {
        this.lmsTabChange.emit(lmsEventDetails);
    }

    /**
     * This method invoked when user select one of the suggestions from auto complete
     * @param {string} searchText
     * @memberOf LmsTabsComponent
     */
    onAutoCompleteNotify(searchText: string) {
        this.search(true);
    }

    /**
     * Life cycle hook to clean the states
     * @memberOf LmsTabsComponent
     */
    ngOnDestroy() { }

    /**
     * Convert Terminal Milepost to Terminal Mnemonic
     * @private
     * @param {string} value
     * @returns {string}
     * @memberOf LmsTabsComponent
     */
    private getConvertedValue(value: string): string {
        if (!value || value === '' || value === ' ') {
            return '';
        }
        if (value === 'DELETE' || value === 'OFFLN' || value === 'NEW' || value === 'PARKED') {
            return value;
        }
        let emitValue = value;
        const commonopsPicklist = this.staticDataStoreService.getValue('commonopsPicklist');
        if (commonopsPicklist) {
            const data = commonopsPicklist.mapOfMnemonis ? commonopsPicklist.mapOfMnemonis : null;
            if (data) {
                if (data[value]) {
                    emitValue = data[value];
                }
            }
        }
        return emitValue;
    }

    /**
     * @private
     * @param {any} str
     * @param {any} padString
     * @param {any} max
     * @returns {string}
     *
     * @memberOf LmsTabsComponent
     */
    private rightPad(str, padString, max): string {
        str = str.toString();
        return str.length < max ? this.rightPad(str + padString, padString, max) : str;
    }

    /**
     * To check whether string is a number or not
     * @private
     * @param {string} value
     * @returns {boolean}
     * @memberOf LmsTabsComponent
     */
    private isNumeric(value: string): boolean {
        const v = value.match(/^\d+$/);
        if (v) {
            if (v.toString() === value) {
                return true;
            }
        }
        return false;
    }
    /**
     * Convert string into standard CSX Loco Id format
     * @private
     * @param {string} value
     * @memberOf LmsTabsComponent
     */
    private formatLocomotiveId(value: string) {
        let formattedValue = value;
        if (value) {
            value = value.trim();
            if (this.isNumeric(value.charAt(0))) {
                formattedValue = this.defaultLocoInitial + ' ' + value.replace(/^0+/, '');
            } else {
                const v = value.match(/\d+/);
                if (v) {
                    const locoInitial = (v.index <= 4) ? value.substring(0, v.index).trim() : value.substring(0, 4).trim();
                    const locoNumber = (v.index <= 4) ?
                        value.substring(v.index, value.length).trim() : value.substring(4, value.length).trim();

                    if (this.isNumeric(locoNumber)) {
                        formattedValue = locoInitial + ' ' + locoNumber.replace(/^0+/, '');
                    }
                }
            }
        }
        return formattedValue;
    }

    /**
   * check for valid locomotive id
   * @param {string} value
   * @memberOf LocoDataService
   */
    isValidLocomotiveId(value: string): boolean {
        if (value) {
            const v = value.match(/\d+/);
            if (v) {
                const letter = value.substring(0, v.index).match(/^[A-Za-z\s]+$/);
                if (letter &&
                    value.substring(0, v.index).trim().length >= 2 && value.substring(0, v.index).trim().length <= 4
                    && value.length <= 14) {
                    const locoNumber = (v.index <= 4) ?
                        value.substring(v.index, value.length).trim() : value.substring(4, value.length).trim();
                    if (locoNumber && locoNumber.length <= 6 && this.isNumeric(locoNumber)) {
                        return true;
                    } else {
                        return false;
                    }
                }
            }
        }
    }

    /**
   * Check for train id.
   * @param {string} value
   * @returns {boolean}
   * @memberOf LmsTabsComponent
   */
    isValidTrainId(value: string): boolean {
        let flag = false;
        if (value && value.length === 6) {
            if (!this.isNumeric(value.charAt(0))) {
                const remainingStr = value.substring(1, value.length);
                if (this.isNumeric(remainingStr)) {
                    flag = true;
                }
            }
        }
        return flag;
    }

    /**
     * Update Search Text
     * @param {string} event
     * @memberOf LmsTabsComponent
     */
    updateSearchText(event: string) {
        this.isChangeSearch = true;
        this.searchText = event ? event.toUpperCase() : event;
    }

    /**
     * @memberOf LmsTabsComponent
     */
    hideWarnPopup() {
        this.showWarnPop = false;
        this.warnMessage = '';
        this.popUpService.setPageMode('');
        this.popUpService.setSubmitBtnId('');
    }

    setPageMode(btnId: string) {
        if (btnId && btnId.length > 0) {
            this.popUpService.setPageMode('Dialog');
            this.popUpService.setSubmitBtnId(btnId);
        }
    }

    /**
     * @memberOf LmsTabsComponent
     */
    goToRefresh() {
        const editModeStatus = {
            status: false,
            message: ''
        };
        this.editModeService.setEditMode(editModeStatus);
        this.hideWarnPopup();
        setTimeout(() => {
            this.search(true);
        }, 30);
    }

    /**
     * @param {any} e
     * @returns
     *
     * @memberOf LmsTabsComponent
     */
    dragOver(e) {
        if (e) {
            e.preventDefault();
        }
        return false;
    }

    /**
     * @param {any} e
     * @param {any} dropId
     * @returns
     *
     * @memberOf LmsTabsComponent
     */
    drop(e, dropId) {
        if (e) {
            e.stopPropagation();
        }
        if (!this.lmsTabsDragDropService.tabDragStarted) {
            if (this.tabService.isAddTabAllowed(this.selectedTab.type)) {
                const localStorageTabJson = localStorage.getItem('localTab');
                localStorage.removeItem('localTab');
                let localStorageData;
                let localStorageTab;
                if (localStorageTabJson) {
                    localStorageTab = JSON.parse(localStorageTabJson);
                    localStorageData = localStorageTab.dropData;
                    delete localStorageTab.dropData;
                }
                this.dataStoreService.putValue(localStorageTab, localStorageData);
                this.tabService.copyTab(localStorageTab, this.editModeService.isEditMode);
                this.lmsTabsDragDropService.setTabDragStarted(false);
            } else {
                this.messageHandlerService.handleMessage(<MessageDetails>({
                    type: this.configService.getConfigData().message.type.client,
                    level: this.configService.getConfigData().message.level.error,
                    target: this.configService.getConfigData().message.target.page,
                    message: this.configService.getConfigData().messages.maxTab
                }));
            }
        }
        return false;
    }

    private processCursorPosition() {
        if (this.selectedTab) {
            if (this.selectedTab.type === 'Locomotive' || this.selectedTab.type === 'Train') {
                if (this.selectedTab.subType !== 'Notes') {
                    setTimeout(() => {
                        const searchText = document.getElementById('searchTextBox' + 'topHeader');
                        if (searchText) {
                            searchText.focus();
                        }
                    }, 10);
                } else if (this.selectedTab.subType === 'Notes') {
                    let id = '';
                    if (this.selectedTab.subTypeDetail.notes.status === 'loaded' || this.selectedTab.subTypeDetail.notes.status === 'refresh') {
                        id = 'addNoteTextBox';
                    } else {
                        id = 'searchTextBox' + 'topHeader';
                    }
                    if (id) {
                        setTimeout(() => {
                            const searchText = document.getElementById(id);
                            if (searchText) {
                                searchText.focus();
                            }
                        }, 10);
                    }
                }
            } else {
                if (this.selectedTab.type === 'Terminal') {
                    //if (this.selectedTab.subType !== 'Notes') {
                    setTimeout(() => {
                        const autoComplBox = document.getElementById('autoCompleteText' + 'topHeader');
                        if (autoComplBox) {
                            autoComplBox.focus();
                        }
                    }, 10);
                    //}

                    /*else if (this.selectedTab.subType === 'Notes') {
                        let id = '';
                        if (this.selectedTab.subTypeDetail.notes.status === 'loaded' || this.selectedTab.subTypeDetail.notes.status === 'refresh') {
                            id = 'addNoteTextBox';
                        } else {
                            id = 'autoCompleteText' + 'topHeader';
                        }
                        if (id) {
                            setTimeout(() => {
                                const searchText = document.getElementById(id);
                                if (searchText) {
                                    searchText.focus();
                                }
                            }, 10);
                        }
                    }*/
                } else if (this.selectedTab.type === 'Outlying-Points') {
                    let id = '';
                    if (this.selectedTab.subTypeDetail.data.status === 'loaded' || this.selectedTab.subTypeDetail.data.status === 'refresh') {
                        id = 'outlyingTableFilterBox';
                    } else {
                        id = 'outlyingPointDeskId' + 'topHeader';
                    }
                    if (id) {
                        setTimeout(() => {
                            const searchText = document.getElementById(id);
                            if (searchText) {
                                searchText.focus();
                            }
                        }, 10);
                    }
                } else if (this.selectedTab.type === 'Yard-Local') {
                    setTimeout(() => {
                        let id = '';
                        if (this.selectedTab.subTypeDetail.data.status === 'loaded' || this.selectedTab.subTypeDetail.data.status === 'refresh') {
                            id = 'defaultSearchButton';
                        } else {
                            id = 'yardAndLocalDropDown';
                        }
                        const dropDownElem = document.getElementById(id);
                        if (dropDownElem) {
                            dropDownElem.focus();
                        }
                    }, 20);
                }
            }
        }
    }
}
